/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @angular-eslint/no-output-on-prefix */
import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { from, Observable, Subscription } from 'rxjs';
import { mergeMap, take, toArray } from 'rxjs/operators';
import { AuthService } from 'src/modules/authentication/services/auth.service';
import { SentenceTypeEnum } from 'src/modules/shared/models/enums/sentence-type.enum';
import { GroupConfiguration } from 'src/modules/shared/models/group-keys.model';
import { ClassificationParameterTypeEnum } from 'src/modules/shared/models/interfaces/classification-parameters.interface';
import { IFileDocument, IFilePageAndWords } from 'src/modules/shared/models/interfaces/files.interface';
import {
  IRobotExtracted,
  IRobotPreviewConfiguration,
  IRobotSentence,
  IRobotWordClick,
  TDocumentSentencesAndWordClicks,
} from 'src/modules/shared/models/interfaces/robots.interface';
import { ISmartCanvasClickedItemEvent, ISmartCanvasOptions } from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import {
  ClassificationParameters,
  Polygon,
  RobotPreview,
  RobotPreviewConfiguration,
  RobotPreviewSentenceConfiguration,
  RobotPreviewWordClickConfiguration,
  RobotSentence,
  RobotWordClick,
} from 'src/modules/shared/models/robots.model';
import { SmartCanvasHighlightedArea, SmartCanvasOptions } from 'src/modules/shared/models/smart-canvas.model';
import { EventEmitterService } from 'src/modules/shared/services/event-emitter.service';
import { FilesService } from 'src/modules/shared/services/files.service';
import { RobotsService } from 'src/modules/shared/services/robots.service';
import { IRobotCreationStates } from '../../models/robots.states';
import { IClassificationParameters } from 'src/modules/shared/models/interfaces/classification-parameters.interface';
import { WordDataTypeEnum } from 'src/modules/shared/models/enums/word-data-type.enum';

@Component({
  selector: 'robot-creation',
  templateUrl: './robots-root.component.html',
  styleUrls: ['./robots-root.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class RobotsRootComponent implements OnChanges {
  @Input() fileId!: string;
  @Input() robotId!: string;
  @Input() niboToken!: string;
  @Input() paramsGroups!: GroupConfiguration[];
  @Input() options!: ISmartCanvasOptions;
  @Input() classificationId?: string;
  @Output() exit = new EventEmitter<void>();
  @Output() onRobotCreated = new EventEmitter<any>();
  @Output() onRobotEdited = new EventEmitter<any>();
  @Output() onRobotLoaded = new EventEmitter<any>();
  @Output() onLoadedRobotsFromFile = new EventEmitter<IRobotExtracted[]>();
  @Output() clickedValue: EventEmitter<ISmartCanvasClickedItemEvent> = new EventEmitter();

  public smartCanvasOptions!: SmartCanvasOptions;
  public selectedFile!: IFileDocument;
  public selectedRobot!: ClassificationParameters;
  public loadedRobotsFromFile: IRobotExtracted[] = [];
  public selectedRobotFiles: IFileDocument[] = [];
  public selectedDocumentSentencesAndWordClicks: TDocumentSentencesAndWordClicks = { sentences: [], wordClicks: [] };
  public selectedRobotPreviewConfig: IRobotPreviewConfiguration[] = [];
  public selectedRobotFilesPreviewsList: RobotPreview[] = [];
  public selectedRobotFilePreview!: RobotPreview;
  public selectedFilePagesAndWords: IFilePageAndWords[];
  public selectedGroupConfiguration!: GroupConfiguration;
  public states: { [key: string]: boolean } = {
    loadingFiles: false,
  };

  private _defaultSentenceGroupName = 'stc';
  private _defaultWordclicksGroupName = 'wrd';
  private _subscriptions: { [key: string]: Subscription } = {};
  private _defaultOptions: SmartCanvasOptions;

  constructor(
    private _filesService: FilesService,
    private _robotsService: RobotsService,
    private _toastr: ToastrService,
    private _eventEmitterService: EventEmitterService,
    private _authService: AuthService
  ) {
    this._defaultOptions = new SmartCanvasOptions();
    this._defaultOptions.enableAreaSelection = false;
    this._defaultOptions.splitWords = false;
    this._defaultOptions.showRobotSidebar = false;
    this.smartCanvasOptions = this._defaultOptions;

    this._eventEmitterService.get<ISmartCanvasClickedItemEvent>('clicked-value').subscribe((click) => {
      this.clickedValue.emit(click);
    });
  }

  @HostListener('addSentenceHighlight', ['$event.detail'])
  private _addSentenceHighlight(data: IRobotSentence): void {
    this._eventEmitterService.get<SmartCanvasHighlightedArea>('add-canvas-highlight').emit(
      new SmartCanvasHighlightedArea(
        this._defaultSentenceGroupName,
        new RobotSentence({
          text: data.text,
          polygon: new Polygon(data.polygon.boundingBox),
          type: SentenceTypeEnum.Default,
          isDenied: false,
        }),
        this.smartCanvasOptions.customStyles.sentences,
        data.identificationThreshold
      )
    );
  }

  @HostListener('addWordClickHighlight', ['$event.detail'])
  private _addWordClickHighlight(data: IRobotWordClick): void {
    this._eventEmitterService
      .get<SmartCanvasHighlightedArea>('add-canvas-highlight')
      .emit(
        new SmartCanvasHighlightedArea(
          this._defaultWordclicksGroupName,
          new RobotWordClick(data),
          this.smartCanvasOptions.customStyles.wordClicks,
          data.extractionThreshold
        )
      );
  }

  @HostListener('removeSentenceHighlight', ['$event.detail'])
  private _removeSentenceHighlight(data: IRobotSentence): void {
    const highlightedArea = new SmartCanvasHighlightedArea(
      this._defaultSentenceGroupName,
      new RobotSentence(data),
      this.smartCanvasOptions.customStyles.sentences
    );
    this._eventEmitterService.get('remove-canvas-highlight').emit(highlightedArea.id);
  }

  @HostListener('removeWordClickHighlight', ['$event.detail'])
  private _removeWordClickHighlight(data: IRobotWordClick): void {
    const highlightedArea = new SmartCanvasHighlightedArea(
      this._defaultWordclicksGroupName,
      new RobotWordClick(data),
      this.smartCanvasOptions.customStyles.wordClicks
    );
    this._eventEmitterService.get('remove-canvas-highlight').emit(highlightedArea.id);
  }

  @HostListener('createRobot', ['$event.detail'])
  private _createRobot(detail: {
    data: IClassificationParameters;
    callback: (robotResult: IClassificationParameters) => void;
    errorCallback: (error: string) => void;
  }): void {
    const classificationParameters = new ClassificationParameters(detail.data);
    this._robotsService.createCustomRobot(classificationParameters).subscribe(
      (result) => {
        detail.callback(result);
      },
      (error) => {
        detail.errorCallback(error as string);
      }
    );
  }

  @HostListener('editRobot', ['$event.detail'])
  private _editRobot(detail: {
    data: IClassificationParameters;
    callback: (robotResult: IClassificationParameters) => void;
    errorCallback: (error: string) => void;
  }): void {
    const classificationParameters = new ClassificationParameters(detail.data);
    this._robotsService.editCustomRobot(classificationParameters.id, classificationParameters).subscribe(
      (result) => {
        detail.callback(result);
      },
      (error) => {
        detail.errorCallback(error as string);
      }
    );
  }

  private _getRobotsInformations(): void {
    if (this.classificationId) {
      this._subscriptions['getCustomRobot'] = this._robotsService
        .getCustomRobot(this.classificationId, this.robotId)
        .subscribe((classificationParameters) => {
          if (classificationParameters) {
            this.selectedRobot = new ClassificationParameters(classificationParameters);
            this.selectedDocumentSentencesAndWordClicks = {
              sentences: this.selectedRobot.sentences,
              wordClicks: this.selectedRobot.wordClicks,
            };

            this._getRobotsFiles();
          } else {
            this.selectedRobot = null;
            this.selectedRobotFiles = [];
            this.selectedRobotPreviewConfig = [];
            this.selectedDocumentSentencesAndWordClicks = { sentences: [], wordClicks: [] };
            this.selectedRobotFilePreview = null;
            this.selectedRobotFilesPreviewsList = [];
          }

          this.onRobotLoaded.emit(this.selectedRobot);
        });
    }

    if (this.fileId && this.robotId) {
      this._subscriptions['getLatestExtractionFromFile'] = this._robotsService
        .getLatestExtractionResultFromFile(this.fileId, this.robotId)
        .subscribe((extractionResult) => {
          this.loadedRobotsFromFile = extractionResult.robots;
          this.onLoadedRobotsFromFile.emit(this.loadedRobotsFromFile);
        });
    }
  }

  private _loadFileDetails(fileId: string): void {
    if (this._subscriptions['loadFileDetails.getFile']) {
      this._subscriptions['loadFileDetails.getFile'].unsubscribe();
    }

    if (fileId) {
      this._getPageAndWords(fileId);

      this._subscriptions['loadFileDetails.getFile'] = this._filesService.getFile(fileId).subscribe(
        (file) => {
          this.selectedFile = file;
        },
        (error: { error: { Message: string } }) => {
          this._toastr.error(error.error.Message ?? 'Erro ao carregar o arquivo.');
        }
      );
    }
  }

  private _getRobotsFiles(fileIds: string[] = this.selectedRobot.fileIds): void {
    if (fileIds.length) {
      this._subscriptions['getRobotsFiles'] = from(fileIds)
        .pipe(
          mergeMap((fileId) => {
            return this._filesService.getFile(fileId);
          }),
          toArray(),
          take(1)
        )
        .subscribe((robotFiles) => {
          this.selectedRobotFiles = robotFiles;
          this.previewRobot();
        });
    }
  }

  private _getPageAndWords(fileId: string): void {
    let obs: Observable<IFilePageAndWords[]>;

    if (this._subscriptions['loadFileDetails.getPagesAndWords']) {
      this._subscriptions['loadFileDetails.getPagesAndWords'].unsubscribe();
    }

    if (this.selectedGroupConfiguration?.keys.find((k) => k.type === WordDataTypeEnum.table)) {
      obs = this._robotsService.getPagesAndWordsAndTables(fileId, {
        limit: this.smartCanvasOptions.maxPagesToRead,
        offset: 0,
      });
    } else if (!this.selectedFilePagesAndWords) {
      obs = this._robotsService.getPagesAndWords(fileId, {
        limit: this.smartCanvasOptions.maxPagesToRead,
        offset: 0,
      });
    }

    if (obs) {
      this._subscriptions['loadFileDetails.getPagesAndWords'] = obs.subscribe((wordsAndPages) => {
        this.selectedFilePagesAndWords = wordsAndPages;
      });
    }
  }

  public robotCreationStateChanged(robotCreationState: {
    stateChanged: string;
    newValue: any;
    states: IRobotCreationStates;
    data?: any;
  }): void {
    if (robotCreationState.stateChanged === 'step') {
      if (robotCreationState.newValue === 2) {
        this.smartCanvasOptions.showGroupKeys = true;
        this.smartCanvasOptions.showGroupKeysOnHover = true;
      } else {
        this.smartCanvasOptions.showGroupKeys = false;
        this.smartCanvasOptions.showGroupKeysOnHover = false;
      }

      if (robotCreationState.newValue === 1 || robotCreationState.newValue === 2) {
        this.smartCanvasOptions.enableWordSelection = true;
        this.smartCanvasOptions.showSplitWordsControl = true;
      } else {
        this.smartCanvasOptions.enableWordSelection = false;
        this.smartCanvasOptions.showSplitWordsControl = false;
      }
    } else if (robotCreationState.stateChanged === 'finished') {
      if (robotCreationState.newValue) {
        this.onRobotCreated.emit(robotCreationState.data);
      }
    }
  }

  public selectedGroupConfigurationChange(value: GroupConfiguration): void {
    this.selectedGroupConfiguration = value;
    this.selectedRobotPreviewConfig = this.selectedRobot.fileIds.map(
      (fileId) => new RobotPreviewConfiguration(fileId, this.selectedRobot.sentences, this.selectedGroupConfiguration.keys)
    );
  }

  public exitFromRobot(): void {
    this.exit.emit();
  }

  public previewRobot(
    items: TDocumentSentencesAndWordClicks = this.selectedDocumentSentencesAndWordClicks,
    robotPreviewConfig?: IRobotPreviewConfiguration[]
  ): void {
    this.selectedDocumentSentencesAndWordClicks = items;
    this.selectedRobotFilePreview = null;

    this.selectedRobotPreviewConfig.forEach((previewConfig) => {
      // Removendo items que não existem mais na configuração do robô
      previewConfig.sentencesConfiguration = previewConfig.sentencesConfiguration.filter((sentenceConfiguration) =>
        items.sentences.find((stc) => stc === sentenceConfiguration.sentence)
      );

      // Adicionando novos items que foram adicionados na configuração do robô
      items.sentences
        .filter((sentences) => !previewConfig.sentencesConfiguration.find((stc) => stc.sentence === sentences))
        .forEach((item) => {
          previewConfig.sentencesConfiguration.push(new RobotPreviewSentenceConfiguration(item));
        });

      // Atualizando items que foram alterados na configuração do robô
      previewConfig.sentencesConfiguration.forEach((sentenceConfiguration) => {
        const item = items.sentences.find((stc) => stc === sentenceConfiguration.sentence);
        if (item) {
          sentenceConfiguration.sentence = item;
        }
      });

      // Removendo wordclicks que não existem mais na configuração do robô
      previewConfig.wordclicksConfiguration = previewConfig.wordclicksConfiguration.filter((wordclickConfiguration) =>
        items.wordClicks.find((wcc) => wcc.word.key === wordclickConfiguration.key)
      );

      // Adicionando novos wordclicks que foram adicionados na configuração do robô
      items.wordClicks
        .filter((wordClick) => !previewConfig.wordclicksConfiguration.find((wcc) => wcc.key === wordClick.word.key))
        .forEach((item) => {
          previewConfig.wordclicksConfiguration.push(new RobotPreviewWordClickConfiguration(item.word.key, null));
        });
    });

    if (this.selectedDocumentSentencesAndWordClicks.sentences && this.selectedRobot?.type !== ClassificationParameterTypeEnum.xPath) {
      if (this._subscriptions['previewRobot']) {
        this._subscriptions['previewRobot'].unsubscribe();
      }

      if (this.selectedRobotPreviewConfig.length < 1) return;
      this._subscriptions['previewRobot'] = this._robotsService
        .previewRobot(
          this.selectedDocumentSentencesAndWordClicks.sentences,
          this.selectedDocumentSentencesAndWordClicks.wordClicks,
          robotPreviewConfig ? robotPreviewConfig : this.selectedRobotPreviewConfig
        )
        .subscribe((results) => {
          this.selectedRobotFilesPreviewsList = results;
          this.selectedRobotFilePreview = this.selectedRobotFilesPreviewsList.find((file) => file.fileId === this.fileId);
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options?.currentValue) {
      this.smartCanvasOptions = { ...this._defaultOptions, ...this.options };
      this.smartCanvasOptions.customStyles = { ...this._defaultOptions.customStyles, ...this.options.customStyles };
    }
    
    if (changes.niboToken?.currentValue) {
      this._authService.injectToken(this.niboToken).subscribe(() => {
        if (this.fileId) {
          this._loadFileDetails(this.fileId);
        }
        if (this.classificationId) {
          this._getRobotsInformations();
        }
      });
    } else {
      if (changes.fileId?.currentValue) {
        this._loadFileDetails(this.fileId);
      }
      
      if (changes.classificationId?.currentValue) {
        this._getRobotsInformations();
      }
    }
  }
}
